import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import ConfirmationModal from '../common/ConfirmationModal';
import Loader from '../common/Loader';

import { adminApi } from '../../api';
import { toTitleCase } from '../../utils';
import { APP_FEATURE_CATEGORY_MAP } from '../../constants';

function CustomCheckBox({ option, enabledFeatures, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const handleClick = async () => {
    setSubmitting(true);
    setShowSubmitModal(true);
  };

  const handleSubmit = async (feature) => {
    await onSubmit(feature);
    setSubmitting(false);
    setShowSubmitModal(false);
  };

  const handleCancel = () => {
    setSubmitting(false);
    setShowSubmitModal(false);
  };
  const disabled = submitting;

  return (
    <div key={option} className="my-2">
      <label
        htmlFor={option}
        className={`d-flex align-items-center${disabled ? ' text-info' : ''}`}
      >
        <div style={{ width: '2.5rem' }}>
          {submitting ? (
            <Spinner animation="border" role="status" variant="info" size="sm" className="ms-2" />
          ) : (
            <Form.Check
              className="ms-0"
              type="switch"
              id={`switch-${option}`}
              onChange={() => handleClick()}
              checked={enabledFeatures.includes(option)}
              disabled={disabled}
            />
          )}
        </div>
        {toTitleCase(option)}
      </label>

      <ConfirmationModal
        setShow={handleCancel}
        handleSubmit={() => handleSubmit(option)}
        show={showSubmitModal}
      />
    </div>
  );
}

export default function AppFeaturesForm({ onSubmit, enabledFeatures }) {
  const emptyFeatures = { kyc: [], instant: [], transaction: [] };
  const [options, setOptions] = useState(emptyFeatures);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation();

  const formatFeatures = (featureList) => {
    const featureObj = JSON.parse(JSON.stringify(emptyFeatures));
    featureList.forEach((feature) => {
      if (feature in APP_FEATURE_CATEGORY_MAP) {
        featureObj[APP_FEATURE_CATEGORY_MAP[feature]].push(feature);
      } else {
        featureObj.transaction.push(feature);
      }
    });
    return featureObj;
  };

  useEffect(() => {
    const fetchAppFeatures = async () => {
      const response = await adminApi.fetchAppFeatures();
      const formattedFeatures = formatFeatures(response);
      setOptions(formattedFeatures);
      setLoaded(true);
    };
    fetchAppFeatures();
  }, []);

  if (!loaded) return <Loader />;

  return (
    <Row>
      <Col>
        <div role="group" aria-labelledby="checkbox-group" className="mb-3">
          <span className="text-info">
            {t(`admin.applications.detail.enabledAppFeatures.instant`)}
          </span>
          {options.instant.map((option) => (
            <CustomCheckBox
              key={option}
              option={option}
              enabledFeatures={enabledFeatures}
              onSubmit={onSubmit}
            />
          ))}
        </div>
      </Col>
      <Col>
        <div role="group" aria-labelledby="checkbox-group" className="mb-3">
          <span className="text-info">
            {t(`admin.applications.detail.enabledAppFeatures.transaction`)}
          </span>
          {options.transaction.map((option) => (
            <CustomCheckBox
              key={option}
              option={option}
              enabledFeatures={enabledFeatures}
              onSubmit={onSubmit}
            />
          ))}
        </div>
      </Col>
      <Col>
        <div role="group" aria-labelledby="checkbox-group" className="mb-3">
          <span className="text-info">{t(`admin.applications.detail.enabledAppFeatures.kyc`)}</span>
          {options.kyc.map((option) => (
            <CustomCheckBox
              key={option}
              option={option}
              enabledFeatures={enabledFeatures}
              onSubmit={onSubmit}
            />
          ))}
        </div>
      </Col>
    </Row>
  );
}
